import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { useIsDesktop } from '~/context/common/DeviceContext';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import isEmpty from '~/utilities/isEmpty';
import {
  FILTER_DESKTOP_SCROLL_CONTAINER_ID,
  FILTER_MODAL_SCROLL_CONTAINER_ID
} from './constants';

type OwnProps = React.PropsWithChildren<{
  alwaysRenderChildren?: boolean; // Always render children vs conditional rendering. Use true for components that need immediate loading (like Budget needing to mount immeditely for lazy loading)
  borderless?: boolean;
  className?: string;
  testId: string;
  defaultExpanded?: boolean;
  facetContent: any;
  facetName: string;
}>;

function FilterDrawer({
  alwaysRenderChildren = false,
  borderless = false,
  children,
  className = '',
  defaultExpanded = false,
  testId,
  facetContent,
  facetName
}: OwnProps) {
  const isDesktop = useIsDesktop();
  const drawerRef = useRef<HTMLDivElement>(null);
  const hasContent = !isEmpty(facetContent);
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;

    const timeoutId = setTimeout(() => {
      const drawerElement = drawerRef.current;
      if (!drawerElement) return;

      const rect = drawerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.top > viewportHeight * 0.6) {
        const scrollableParent = document.getElementById(
          isDesktop
            ? FILTER_DESKTOP_SCROLL_CONTAINER_ID
            : FILTER_MODAL_SCROLL_CONTAINER_ID
        );

        if (scrollableParent) {
          const appliedFiltersHeight = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              '--applied-filters-height'
            ) || '0'
          );

          const drawerTop = drawerElement.offsetTop;
          let targetPosition: number;

          if (appliedFiltersHeight > 0) {
            targetPosition = appliedFiltersHeight + 40;
          } else {
            targetPosition = viewportHeight * 0.3;
          }

          scrollableParent.scrollTo({
            top: Math.max(0, drawerTop - targetPosition),
            behavior: 'smooth'
          });
        }
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isOpen, isDesktop]);

  const clickAttributes = useMemo(
    () => ({
      action: 'clicked',
      item: `menu bar ${isOpen ? 'collapse' : 'expand'}`,
      label: facetName
    }),
    [facetName, isOpen]
  );

  return (
    <div
      ref={drawerRef}
      className={cx(
        'bg-background-default ',
        borderless ? '' : 'border-b border-border-subtle'
      )}
    >
      {hasContent && (
        <div data-testid={testId}>
          <Clickable
            aria-expanded={isOpen}
            data-adobe-id="filter-menu-expand-collapse"
            data-analytics-id="Filter Menu"
            data-analytics-attributes={JSON.stringify(clickAttributes)}
            data-testid="filter-menu-expand-collapse"
            onClick={() => setIsOpen(!isOpen)}
            className={cx(
              'flex items-center justify-between p-2xl text-text-strong transition duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] t-strong-sm hover:text-text-strong-hover',
              {
                'shadow-[0_4px_20px_-8px_rgba(106,_115,_125,_0.16),_0_2px_4px_-1px_rgba(106,_115,_125,_0.15)]':
                  isOpen
              }
            )}
          >
            {facetName}
            <ChevronDown
              size={16}
              className={cx(
                'transition-all duration-500 hover:text-text-strong-hover',
                isOpen && 'rotate-180'
              )}
            />
          </Clickable>
          {alwaysRenderChildren ? (
            <motion.div
              className={cx('overflow-hidden px-2xl', className)}
              initial={{ height: isOpen ? 'auto' : 0 }}
              animate={{
                height: isOpen ? 'auto' : 0
              }}
              transition={{ duration: 0.3 }}
            >
              <div className="py-2xl">{children}</div>
            </motion.div>
          ) : (
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  className={cx('overflow-hidden px-2xl', className)}
                  initial={{ height: 0 }}
                  animate={{
                    height: 'auto'
                  }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="py-2xl">{children}</div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      )}
    </div>
  );
}

export default withErrorBoundary(
  FilterDrawer,
  undefined,
  undefined,
  'SRP FilterDrawer'
);
